/**
 * Overlay calendar: generated statewide days minus tenant shadows plus tenant extras.
 * Dates are calendar YYYY-MM-DD. Scope match is date + state/zone, never name.
 */
import { Entity } from '../entity';
import { PublicHoliday, PublicHolidayKind } from './public.holiday';

export type HolidayCalendarSource = 'base' | 'tenant';

export interface HolidayCalendarEntry {
    date: string;
    name: string;
    states: Entity[];
    zones: Entity[];
    source: HolidayCalendarSource;
    kind?: PublicHolidayKind;
    _id?: string;
    honored: boolean;
    /** Local HH:mm when the holiday starts. Missing = the whole calendar day. */
    from?: string;
}

export function holidayTouchesWindow(entry: Pick<HolidayCalendarEntry, 'from'>, window?: { start?: string; end?: string; overnight?: boolean }): boolean {

    if (!entry.from) return true;
    if (!window?.start || !window?.end) return true;
    const holidayStart = clockMinutes(entry.from);
    const windowStart = clockMinutes(window.start);
    let windowEnd = clockMinutes(window.end);
    if (window.overnight || windowEnd <= windowStart) windowEnd += 24 * 60;
    return windowStart < 24 * 60 && windowEnd > holidayStart;
}

function clockMinutes(value: string): number {

    const [hours, minutes] = value.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function holidayKind(holiday: Pick<PublicHoliday, 'kind'>): PublicHolidayKind {

    return holiday.kind === PublicHolidayKind.Shadow ? PublicHolidayKind.Shadow : PublicHolidayKind.Extra;
}

export function sameHolidayState(entry: Entity | undefined, state: string | undefined): boolean {

    if (!entry || !state) return false;
    return (entry.name ?? entry._id ?? '').trim().toLowerCase() === state.trim().toLowerCase();
}

/**
 * Empty states and zones = tenant-wide.
 * Only states = match state. Only zones = match zone id. Both = state AND zone.
 */
export function holidayAppliesTo(holiday: { states?: Entity[]; zones?: Entity[] }, state?: string, zoneIds?: string[]): boolean {

    const states = holiday.states ?? [];
    const zones = holiday.zones ?? [];
    if (!states.length && !zones.length) return true;

    const stateOk = !states.length || (!!state && states.some(entry => sameHolidayState(entry, state)));
    const zoneOk = !zones.length || (!!zoneIds?.length && zones.some(zone => zoneIds.includes(zone._id)));
    return stateOk && zoneOk;
}

export function calendarDateFromHoliday(value: Date | string): string | null {

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        return value.toISOString().slice(0, 10);
    }
    const trimmed = value?.trim();
    if (!trimmed) return null;
    const iso = trimmed.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
}

export function mergeHolidayCalendar(base: HolidayCalendarEntry[], tenant: PublicHoliday[]): HolidayCalendarEntry[] {

    const extras: HolidayCalendarEntry[] = [];
    const shadows: PublicHoliday[] = [];
    for (const row of tenant) {
        if (!row.active && row.active !== undefined) continue;
        if (holidayKind(row) === PublicHolidayKind.Shadow) {
            shadows.push(row);
            continue;
        }
        const date = calendarDateFromHoliday(row.date);
        if (!date) continue;
        extras.push({
            date,
            name: row.name ?? 'Public Holiday',
            states: row.states ?? [],
            zones: row.zones ?? [],
            source: 'tenant',
            kind: PublicHolidayKind.Extra,
            _id: row._id,
            honored: true,
        });
    }

    const mergedBase = base.map(entry => {
        const shadowed = shadows.some(shadow => shadowMatches(shadow, entry));
        return shadowed ? { ...entry, honored: false } : { ...entry, honored: entry.honored !== false };
    });

    const shadowRows: HolidayCalendarEntry[] = shadows.flatMap(shadow => {
        const date = calendarDateFromHoliday(shadow.date);
        if (!date) return [];
        return [{
            date,
            name: shadow.name ?? 'Shadowed holiday',
            states: shadow.states ?? [],
            zones: shadow.zones ?? [],
            source: 'tenant' as const,
            kind: PublicHolidayKind.Shadow,
            _id: shadow._id,
            honored: false,
        }];
    });

    return [...mergedBase, ...shadowRows, ...extras];
}

export function effectiveHolidayDates(entries: HolidayCalendarEntry[], state?: string, zoneIds?: string[], window?: { start?: string; end?: string; overnight?: boolean }): string[] {

    const dates = entries
        .filter(entry => entry.honored && holidayAppliesTo(entry, state, zoneIds) && holidayTouchesWindow(entry, window))
        .map(entry => entry.date);
    return Array.from(new Set(dates)).sort();
}

function shadowMatches(shadow: PublicHoliday, entry: HolidayCalendarEntry): boolean {

    const date = calendarDateFromHoliday(shadow.date);
    if (!date || date !== entry.date) return false;
    const states = shadow.states ?? [];
    const zones = shadow.zones ?? [];
    if (!states.length && !zones.length) return true;
    const stateHit = !states.length || entry.states.some(entryState => states.some(shadowState => sameHolidayState(shadowState, entryState.name ?? entryState._id)));
    const zoneHit = !zones.length || entry.zones.some(entryZone => zones.some(shadowZone => shadowZone._id === entryZone._id));
    if (states.length && !entry.states.length && !zones.length) return true;
    return stateHit && zoneHit;
}
